import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { studioActions, sel } from '../../store/studioStore';
import { useNavigate, useParams } from 'react-router-dom';
import routes from 'router/routes';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import StorageIcon from '@mui/icons-material/Storage';
import PrintIcon from '@mui/icons-material/Print';
import IosShareIcon from '@mui/icons-material/IosShare';
import FolderZipIcon from '@mui/icons-material/FolderZip';

// ── Light-header button styles (matches app header #ffffff background) ─────
const ghost = {
  background: 'none',
  border: 'none',
  color: 'rgba(17,24,39,0.7)',
  borderRadius: 6,
  padding: '5px 10px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  transition: 'all 0.15s',
  whiteSpace: 'nowrap',
};

const outline = {
  ...ghost,
  border: '1px solid rgba(17,24,39,0.2)',
  color: 'rgba(17,24,39,0.85)',
};

const accent = {
  ...ghost,
  background: '#2563eb',
  color: 'white',
  border: 'none',
};

const sep = {
  width: 1,
  height: 20,
  background: 'rgba(17,24,39,0.15)',
  flexShrink: 0,
};

export default function Header({
  onNewDoc,
  onPreview,
  onExportPdf,
  onExportPdfWithoutWhiteSpace,
  onExportZip,
  onOpenDocs,
  onOpenDb,
  scheduleSave,
  onManualSave,
}) {
  const dispatch = useDispatch();
  const zoom = useSelector(sel.zoom);
  const documentName = useSelector(sel.documentName);
  const saveStatus = useSelector(sel.saveStatus);
  const isFullscreen = useSelector(sel.isFullscreen);

  const navigate = useNavigate();
  const { eventId } = useParams();

  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const actionsMenuOpen = Boolean(actionsAnchorEl);

  const handleActionsOpen = (e) => setActionsAnchorEl(e.currentTarget);
  const handleActionsClose = () => setActionsAnchorEl(null);

  const runAction = (action) => () => {
    handleActionsClose();
    action();
  };

  const saveLabels = {
    idle: '',
    saving: '⏳ Sauvegarde…',
    saved: '✓ Sauvegardé',
    error: '✗ Erreur',
  };

  const saveColors = {
    idle: 'rgba(255,255,255,0)',
    saving: '#f59e0b',
    saved: '#86efac',
    error: '#fca5a5',
  };

  const handleZoomChange = (e) => {
    const newZoom = Number(e.target.value) / 100;
    dispatch(studioActions.setZoomLevel(newZoom));
    if (window.__studioApplyZoom) window.__studioApplyZoom(newZoom);
  };

  const handleNameChange = (e) => {
    dispatch(
      studioActions.setDocumentMeta({ name: e.target.value || 'Sans titre' })
    );
    scheduleSave();
  };

  const handleBack = () => {
    if (eventId) {
      navigate(routes.home2.path + `events/${eventId}`);
    } else {
      navigate(-1);
    }
  };

  return (
    <>
      {/* Back */}
      <button style={ghost} onClick={handleBack} title="Retour à l'événement">
        ← Retour
      </button>

      <div style={sep} />

      {/* Document name */}
      <input
        type="text"
        value={documentName}
        onChange={handleNameChange}
        placeholder="Nom du document"
        title="Cliquer pour renommer"
        style={{
          background: 'rgba(17,24,39,0.04)',
          border: '1px solid rgba(17,24,39,0.16)',
          borderRadius: 6,
          color: '#111827',
          fontSize: 13,
          fontWeight: 600,
          padding: '4px 10px',
          outline: 'none',
          width: 180,
          fontFamily: 'inherit',
        }}
      />

      <div style={sep} />

      {/* Actions dropdown */}
      <button
        style={ghost}
        onClick={handleActionsOpen}
        aria-haspopup="true"
        aria-expanded={actionsMenuOpen ? 'true' : undefined}
      >
        Actions
        <KeyboardArrowDownIcon style={{ fontSize: 16 }} />
      </button>
      <Menu
        anchorEl={actionsAnchorEl}
        open={actionsMenuOpen}
        onClose={handleActionsClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 0.5,
            minWidth: 200,
            borderRadius: '8px',
            border: '1px solid #eee',
          },
        }}
      >
        <MenuItem
          onClick={runAction(onNewDoc)}
          sx={{ fontSize: '14px', gap: 1.5, py: 1 }}
        >
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>New</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={runAction(onOpenDocs)}
          sx={{ fontSize: '14px', gap: 1.5, py: 1 }}
        >
          <ListItemIcon>
            <DescriptionIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Documents</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={runAction(() => {
            dispatch(studioActions.addPage());
            scheduleSave();
          })}
          sx={{ fontSize: '14px', gap: 1.5, py: 1 }}
        >
          <ListItemIcon>
            <NoteAddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Page</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={runAction(onOpenDb)}
          sx={{ fontSize: '14px', gap: 1.5, py: 1 }}
        >
          <ListItemIcon>
            <StorageIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Database</ListItemText>
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        <MenuItem
          onClick={runAction(onExportPdf)}
          sx={{ fontSize: '14px', gap: 1.5, py: 1 }}
        >
          <ListItemIcon>
            <PrintIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export Print</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={runAction(onExportPdfWithoutWhiteSpace)}
          sx={{ fontSize: '14px', gap: 1.5, py: 1 }}
        >
          <ListItemIcon>
            <IosShareIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export Share</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={runAction(onExportZip)}
          sx={{ fontSize: '14px', gap: 1.5, py: 1 }}
        >
          <ListItemIcon>
            <FolderZipIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export ZIP</ListItemText>
        </MenuItem>
      </Menu>

      <div style={sep} />

      {/* Zoom */}
      <button
        style={{ ...ghost, padding: '5px 6px' }}
        onClick={() => {
          const z = zoom - 0.1;
          dispatch(studioActions.setZoomLevel(z));
          window.__studioApplyZoom?.(z);
        }}
      >
        −
      </button>
      <input
        type="range"
        min="10"
        max="400"
        value={Math.round(zoom * 100)}
        onChange={handleZoomChange}
        style={{ width: 80, accentColor: '#2563eb', cursor: 'pointer' }}
      />
      <button
        style={{ ...ghost, padding: '5px 6px' }}
        onClick={() => {
          const z = zoom + 0.1;
          dispatch(studioActions.setZoomLevel(z));
          window.__studioApplyZoom?.(z);
        }}
      >
        +
      </button>
      <button
        style={{ ...ghost, padding: '5px 8px', fontSize: 11 }}
        onClick={() => {
          dispatch(studioActions.setZoomLevel(1));
          window.__studioApplyZoom?.(1);
        }}
      >
        Fit
      </button>
      <span style={{ fontSize: 12, color: 'rgba(17,24,39,0.5)', minWidth: 34 }}>
        {Math.round(zoom * 100)}%
      </span>

      <div style={sep} />

      {/* Save status */}
      {saveStatus !== 'idle' && (
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: saveColors[saveStatus],
            minWidth: 90,
          }}
        >
          {saveLabels[saveStatus]}
        </span>
      )}

      {/* Save */}
      <button style={accent} onClick={onManualSave}>
        💾 Sauvegarder
      </button>

      {/* Preview */}
      <button style={outline} onClick={onPreview}>
        👁 Aperçu
      </button>

      {/* Fullscreen */}
      <button
        style={{ ...ghost, padding: '5px 8px' }}
        onClick={() => dispatch(studioActions.setFullscreen(!isFullscreen))}
        title={isFullscreen ? 'Quitter le plein écran' : 'Plein écran'}
      >
        ⛶
      </button>
    </>
  );
}

Header.propTypes = {
  onNewDoc: PropTypes.func.isRequired,
  onPreview: PropTypes.func.isRequired,
  onExportPdf: PropTypes.func.isRequired,
  onExportPdfWithoutWhiteSpace: PropTypes.func.isRequired,
  onExportZip: PropTypes.func.isRequired,
  onOpenDocs: PropTypes.func.isRequired,
  onOpenDb: PropTypes.func.isRequired,
  scheduleSave: PropTypes.func.isRequired,
  onManualSave: PropTypes.func.isRequired,
};
